import type {
  ContentResponse,
  InquiryRecord,
  InquiryStatus,
  JsonValue,
  MediaRecord,
  MediaUploadPayload,
  MailboxForwardingSettings
} from "../types";

export const SESSION_KEY = "atalay_admin_access_key";
export const API_BASE_URL = (
  import.meta.env.VITE_API_BASE_URL || "https://api.atalayllc.com"
).replace(/\/+$/, "");

export class ApiError extends Error {
  status: number;
  details?: unknown;

  constructor(message: string, status: number, details?: unknown) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.details = details;
  }
}

async function readResponse(response: Response): Promise<unknown> {
  if (response.status === 204) return null;
  const contentType = response.headers.get("content-type") || "";
  if (contentType.includes("application/json")) {
    return response.json();
  }
  const text = await response.text();
  return text || null;
}

function errorMessage(payload: unknown, status: number): string {
  if (payload && typeof payload === "object") {
    const record = payload as Record<string, unknown>;
    if (typeof record.message === "string") return record.message;
    if (typeof record.error === "string") return record.error;
  }
  if (typeof payload === "string" && payload.trim()) return payload;
  if (status === 401 || status === 403) return "The access key was not accepted.";
  return `The request failed (${status}).`;
}

export async function apiRequest<T>(
  path: string,
  options: RequestInit = {},
  accessKey = sessionStorage.getItem(SESSION_KEY) || ""
): Promise<T> {
  const headers = new Headers(options.headers);
  headers.set("Accept", "application/json");
  headers.set("x-admin-key", accessKey);
  if (options.body && !(options.body instanceof FormData)) {
    headers.set("Content-Type", "application/json");
  }

  let response: Response;
  try {
    response = await fetch(`${API_BASE_URL}${path}`, {
      ...options,
      headers
    });
  } catch {
    throw new ApiError(
      `Could not reach ${API_BASE_URL}. Check the API deployment and your connection.`,
      0
    );
  }

  const payload = await readResponse(response);
  if (!response.ok) {
    if ((response.status === 401 || response.status === 403) && accessKey) {
      window.dispatchEvent(new CustomEvent("atalay:unauthorized"));
    }
    throw new ApiError(errorMessage(payload, response.status), response.status, payload);
  }
  return payload as T;
}

export const adminApi = {
  dashboard: (key?: string) =>
    apiRequest<Record<string, unknown>>("/api/admin/dashboard", {}, key),

  content: () => apiRequest<ContentResponse>("/api/admin/content"),

  saveDraft: (data: JsonValue) =>
    apiRequest<unknown>("/api/admin/content/draft", {
      method: "PUT",
      body: JSON.stringify({ data })
    }),

  publish: (data: JsonValue) =>
    apiRequest<unknown>("/api/admin/content/publish", {
      method: "POST",
      body: JSON.stringify({ data })
    }),

  inquiries: (status: InquiryStatus | "", limit: number, cursor?: string) => {
    const params = new URLSearchParams({ limit: String(limit) });
    if (status) params.set("status", status);
    if (cursor) params.set("cursor", cursor);
    return apiRequest<unknown>(`/api/admin/inquiries?${params.toString()}`);
  },

  updateInquiry: (
    id: string,
    update: { status: InquiryStatus; notes: string }
  ) =>
    apiRequest<InquiryRecord>(`/api/admin/inquiries/${encodeURIComponent(id)}`, {
      method: "PATCH",
      body: JSON.stringify(update)
    }),

  media: () => apiRequest<unknown>("/api/admin/media"),

  uploadMedia: (payload: MediaUploadPayload) =>
    apiRequest<MediaRecord>("/api/admin/media", {
      method: "POST",
      body: JSON.stringify(payload)
    }),

  deleteMedia: (id: string) =>
    apiRequest<unknown>(`/api/admin/media/${encodeURIComponent(id)}`, {
      method: "DELETE"
    }),

  mailboxForwardingSettings: () =>
    apiRequest<MailboxForwardingSettings>("/api/admin/settings"),

  updateMailboxForwarding: (destinationEmail: string) =>
    apiRequest<MailboxForwardingSettings>("/api/admin/settings", {
      method: "PATCH",
      body: JSON.stringify({ destinationEmail })
    })
};
